/**
 * SPOC org-chart review lifecycle: draft → pending → approved.
 * Legacy: `orgChartBuiltAt` without `orgChartSpocReviewStatus` is treated as approved.
 * @param {object} [wiz]
 * @returns {"draft"|"pending"|"approved"}
 */
export function getOrgChartSpocEffectiveStatus(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const raw = w.orgChartSpocReviewStatus;
  if (raw === "draft" || raw === "pending" || raw === "approved") return raw;
  if (w.orgChartBuiltAt) return "approved";
  return "draft";
}

/**
 * Company artefacts document package review: draft → pending → approved (parallel to org chart review).
 * @param {object} [wiz]
 * @returns {"draft"|"pending"|"approved"}
 */
export function getCompanyArtefactsDocReviewEffectiveStatus(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const raw = w.companyArtefactsDocReviewStatus;
  if (raw === "draft" || raw === "pending" || raw === "approved") return raw;
  return "draft";
}

/** @param {object} [wiz] */
export function isCompanyArtefactsReleasedToDelegates(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  return typeof w.companyArtefactsReleasedToDelegatesAt === "string" && !!w.companyArtefactsReleasedToDelegatesAt.trim();
}

/** Merge patch into `profile.organizationalSetupWizard` (scoping profile JSON). */
export function mergeOrganizationalSetupWizard(profile, patch) {
  const base = profile && typeof profile === "object" ? profile : {};
  const prev =
    base.organizationalSetupWizard && typeof base.organizationalSetupWizard === "object"
      ? base.organizationalSetupWizard
      : {};
  return { ...base, organizationalSetupWizard: { ...prev, ...patch } };
}

export function newSpocPersonnelCsvVersionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `csv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Normalized SPOC personnel CSV versions + selected id (migrates legacy delegateListCsv* when array empty).
 * @param {object} [wiz]
 * @returns {{ versions: Array<{ id: string, version: number, fileName: string, uploadedAt: string, csvText: string }>, selectedId: string }}
 */
export function normalizeSpocPersonnelCsvState(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const raw = Array.isArray(w.spocPersonnelCsvVersions) ? w.spocPersonnelCsvVersions : [];
  /** @type {Array<{ id: string, version: number, fileName: string, uploadedAt: string, csvText: string }>} */
  const versions = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const id = typeof entry.id === "string" && entry.id ? entry.id : null;
    const csvText = typeof entry.csvText === "string" ? entry.csvText : "";
    if (!id || !csvText.trim()) continue;
    const version =
      typeof entry.version === "number" && Number.isFinite(entry.version) && entry.version > 0
        ? entry.version
        : versions.length + 1;
    versions.push({
      id,
      version,
      fileName: typeof entry.fileName === "string" && entry.fileName ? entry.fileName : "personnel.csv",
      uploadedAt: typeof entry.uploadedAt === "string" ? entry.uploadedAt : "",
      csvText,
    });
  }
  versions.sort((a, b) => a.version - b.version || String(a.id).localeCompare(String(b.id)));

  if (versions.length === 0) {
    const legacyText = typeof w.delegateListCsvText === "string" ? w.delegateListCsvText : "";
    if (legacyText.trim()) {
      versions.push({
        id: "legacy-v1",
        version: 1,
        fileName:
          typeof w.delegateListFileName === "string" && w.delegateListFileName ? w.delegateListFileName : "personnel.csv",
        uploadedAt: typeof w.delegateListUploadedAt === "string" ? w.delegateListUploadedAt : "",
        csvText: legacyText,
      });
    }
  }

  let selectedId = typeof w.spocPersonnelCsvSelectedVersionId === "string" ? w.spocPersonnelCsvSelectedVersionId : "";
  if (!selectedId || !versions.some((v) => v.id === selectedId)) {
    selectedId = versions.length ? versions[versions.length - 1].id : "";
  }
  return { versions, selectedId };
}

/**
 * @param {Array<{ id: string, fileName?: string, uploadedAt?: string, csvText: string }>} versions
 * @param {string} selectedId
 */
export function spocPersonnelLegacySyncFromSelection(versions, selectedId) {
  const v = versions.find((x) => x.id === selectedId);
  if (!v) {
    return {
      delegateListCsvText: "",
      delegateListFileName: "",
      delegateListUploadedAt: "",
    };
  }
  return {
    delegateListCsvText: v.csvText,
    delegateListFileName: v.fileName || "personnel.csv",
    delegateListUploadedAt: v.uploadedAt || "",
  };
}

export function orgSetupSubtabPersonaKeyFromRole(role) {
  const r = String(role ?? "")
    .toUpperCase()
    .replace(/-/g, "_");
  if (r === "LEAD_ADVISOR") return "leadAdvisor";
  if (r === "COMPANY_SPOC") return "companySpoc";
  if (r === "COMPANY_DELEGATE") return "companyDelegate";
  if (r === "ADVISOR" || r === "ADMIN") return "advisorWorkbench";
  return null;
}

/** @param {{ key: string }[]} navList */
export function orgSetupNavTabIndex(navList, tabKey) {
  if (!Array.isArray(navList) || navList.length === 0) return -1;
  const k = String(tabKey ?? "");
  const i = navList.findIndex((s) => s && String(s.key) === k);
  return i >= 0 ? i : -1;
}

/** @param {object} wiz */
export function getOrgSetupSubtabFurthestIndex(wiz, personaKey) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const raw = w.orgSetupSubtabFurthestIndexByPersona;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 0;
  const n = Number(raw[personaKey]);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

/**
 * @param {{ key: string }[]} navList
 * @param {object} wiz
 * @param {string} personaKey
 * @param {string} activeTabKey
 */
export function isOrgSetupSubtabContentSealedReadOnly(navList, wiz, personaKey, activeTabKey) {
  if (!personaKey) return false;
  const idx = orgSetupNavTabIndex(navList, activeTabKey);
  if (idx < 0) return false;
  const furthest = getOrgSetupSubtabFurthestIndex(wiz, personaKey);
  return idx < furthest;
}

/**
 * @param {object} wiz
 * @param {string} personaKey
 * @param {number} visitedIndex — 0-based index in the persona’s nav list
 * @returns {{ orgSetupSubtabFurthestIndexByPersona: Record<string, number> } | null}
 */
export function patchBumpOrgSetupSubtabFurthest(wiz, personaKey, visitedIndex) {
  if (!personaKey || typeof visitedIndex !== "number" || !Number.isFinite(visitedIndex) || visitedIndex < 0)
    return null;
  const vi = Math.floor(visitedIndex);
  const prev = getOrgSetupSubtabFurthestIndex(wiz, personaKey);
  if (vi <= prev) return null;
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const oldBy =
    w.orgSetupSubtabFurthestIndexByPersona && typeof w.orgSetupSubtabFurthestIndexByPersona === "object" &&
    !Array.isArray(w.orgSetupSubtabFurthestIndexByPersona)
      ? { ...w.orgSetupSubtabFurthestIndexByPersona }
      : {};
  return { orgSetupSubtabFurthestIndexByPersona: { ...oldBy, [personaKey]: vi } };
}
